import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { resolveCoverUrl } from '../../services/api';
import styles from './StudyNotesPage.module.css';
import { AlertIcon, EditIcon } from '../../components/Icons';
import LoadingSpinner from '../../components/LoadingSpinner';
import StudyHeader from './StudyHeader';

const StudyNotesPage = () => {
  const navigate = useNavigate();

  const [noteGroups, setNoteGroups] = useState([]);
  const [bookMetadata, setBookMetadata] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchNoteGroupsSummary = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.get('/user-notes/summary', {
        params: { limit: 50 },
      });
      const groups = Array.isArray(response.data?.groups) ? response.data.groups : [];
      const normalized = groups.map((group) => ({
        key: String(group.book_id ?? group.bookId ?? 'unknown'),
        bookId: group.book_id ?? group.bookId ?? null,
        bookTitle: group.book_title || group.bookTitle || '未命名书籍',
        itemCount: group.item_count ?? group.itemCount ?? 0,
      }));

      setNoteGroups(normalized);

      // Fetch book covers
      const ids = normalized
        .map((g) => g.bookId)
        .filter((id) => id != null)
        .map((id) => String(id));
      const uniqueIds = Array.from(new Set(ids));
      if (uniqueIds.length) {
        const responses = await Promise.allSettled(
          uniqueIds.map(async (id) => {
            const res = await api.get(`/novels/${id}`);
            return { id, data: res.data };
          })
        );
        const meta = {};
        responses.forEach((r) => {
          if (r.status === 'fulfilled') {
            const data = r.value.data || {};
            meta[r.value.id] = {
              ...data,
              cover_image_url: resolveCoverUrl(data.cover_image_url),
            };
          }
        });
        setBookMetadata(meta);
      }
    } catch (err) {
      console.error('获取收藏笔记统计失败:', err);
      setError('获取收藏笔记失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNoteGroupsSummary();
  }, [fetchNoteGroupsSummary]);

  const handleStartNoteStudy = useCallback(
    (bookId) => {
      if (!bookId) return;
      navigate(`/learn/notes/${encodeURIComponent(bookId)}`);
    },
    [navigate]
  );

  if (loading) {
    return (
      <div className={styles.study}>
        <LoadingSpinner fullPage text="加载收藏内容中..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.study}>
        <div className={styles.noHistory}>
          <div className={styles.noHistoryIcon}><AlertIcon size={48} /></div>
          <h3>无法加载收藏内容</h3>
          <p>{error}</p>
          <button
            className={styles.retryBtn}
            onClick={() => window.location.reload()}
          >
            重试
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.study}>
      <div className={styles.detailView}>
        <StudyHeader title="学习笔记" onBack={() => navigate('/study')} />
        <div className={styles.detailContent}>
          {!noteGroups.length ? (
            <div className={styles.noHistory}>
              <div className={styles.noHistoryIcon}><EditIcon size={48} /></div>
              <h3>还没有收藏的笔记</h3>
              <p>在阅读时使用AI助手并点击心形按钮即可收藏AI回复</p>
            </div>
          ) : (
            <div className={styles.bookList}>
              {noteGroups.map((group) => (
                <div
                  key={group.key}
                  className={styles.bookItem}
                  onClick={() => {
                    if (!group.bookId) return;
                    navigate(`/study/notes?bookid=${encodeURIComponent(group.bookId)}`);
                  }}
                >
                  <div className={styles.bookCover}>
                    {bookMetadata[group.bookId]?.cover_image_url ? (
                      <img
                        src={bookMetadata[group.bookId].cover_image_url}
                        alt={group.bookTitle}
                        loading="lazy"
                      />
                    ) : (
                      <div className={styles.bookCoverPlaceholder}>📖</div>
                    )}
                  </div>
                  <div className={styles.bookInfo}>
                    <span className={styles.bookTabTitle}>{group.bookTitle}</span>
                    <span className={styles.bookTabCount}>{group.itemCount} 条笔记</span>
                  </div>
                  <button
                    type="button"
                    className={styles.studyBtn}
                    onClick={(event) => {
                      event.stopPropagation();
                      handleStartNoteStudy(group.bookId);
                    }}
                  >
                    <span className={`material-icons-outlined text-sm ${styles.studyBtnIcon}`} aria-hidden="true">
                      school
                    </span>
                    去学习
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StudyNotesPage;
